import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useUsers from 'contexts/basicData/useUsers';
import { formatDateTime } from 'utils/date-helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons';
import CaseOpenLink from 'components/CaseOpenLink';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { AssignmentDTO, TranslationLookups } from 'api/model';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import {
  getTransportLogisticsGetAssignmentsEstimatedEndTimePassedQueryKey,
  useTransportLogisticsGetAssignmentsEstimatedEndTimePassed,
} from 'api/transport-logistics/transport-logistics';
import {
  Centered,
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useCaseMarkEventsAsReadByAdmin } from 'api/case/case';
import Checkbox from 'components/inputs/Checkbox';
import TextButton from 'components/inputs/TextButton';
import AvailabilityFormModal from 'pages/Employees/Availability/AvailabilityFormModal';
import useModalStack from 'contexts/modal/useModalStack';
import useBaseAssignmentWidgetColumnSettings from './useBaseAssignmentWidgetColumnSettings';

const TABLE_NAME = 'estimated-end-time-passed';

const EstimatedEndTimePassedWidget = () => {
  const [assignments, setAssignments] = useState<AssignmentDTO[]>([]);
  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const users = useUsers();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();
  const modalStack = useModalStack();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useCaseMarkEventsAsReadByAdmin({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            `${getTransportLogisticsGetAssignmentsEstimatedEndTimePassedQueryKey()}`,
          ]);
        },
      },
    });

  const getAssignments =
    useTransportLogisticsGetAssignmentsEstimatedEndTimePassed(
      {
        startDate: daterange[0] ?? undefined,
        endDate: daterange[1] ?? undefined,
        businessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getAssignments.data) {
      setAssignments(getAssignments.data);
    } else {
      setAssignments([] as AssignmentDTO[]);
    }
  }, [getAssignments.data]);

  const filteredAssignments = useMemo(() => {
    if (!searchText.length) {
      return assignments;
    }
    return assignments
      ? assignments.filter((assignment) => {
          return (
            assignment.registrationNumber
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (users[assignment.assignedToID ?? -1]?.name ?? '')
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        })
      : [];
  }, [assignments, searchText, users]);

  const baseColumnSettings = useBaseAssignmentWidgetColumnSettings();

  const columnSettings: ColumnSetting<AssignmentDTO, TranslationLookups>[] =
    useMemo(() => {
      const columns: ColumnSetting<AssignmentDTO, TranslationLookups>[] =
        baseColumnSettings.concat([
          {
            head: 'Estimerad sluttid',
            render: (row) =>
              `${
                row.startTime && row.estimatedDuration
                  ? formatDateTime(
                      new Date(row.startTime.getTime() + row.estimatedDuration)
                    )
                  : '-'
              }`,
            width: 100,
            // sort by startTime which can be undefined
            sortFunction: (a, b) => {
              if (a.startTime && b.startTime) {
                const aEnd = a.startTime.getTime() + (a.estimatedDuration ?? 0);
                const bEnd = b.startTime.getTime() + (b.estimatedDuration ?? 0);
                return aEnd - bEnd;
              }
              return 0;
            },
          },
          {
            head: 'Fälttestare',
            render: (row) => {
              return (
                <span>
                  {users[row.assignedToID ?? -1] ? users[row.assignedToID ?? -1].name : ''}
                </span>
              );
            },
            width: 100,
            sortFunction: (a, b) =>
              (users[a.assignedToID ?? -1]?.name ?? '').localeCompare(
                users[b.assignedToID ?? -1]?.name ?? ''
              ),
          },
          {
            head: 'Telefonnummer',
            render: (row) => {
              return (
                <span>
                  {users[row.assignedToID ?? -1]
                    ? users[row.assignedToID ?? -1].phoneNumber
                    : ''}
                </span>
              );
            },
            width: 100,
            sortFunction: (a, b) =>
              (users[a.assignedToID ?? -1]?.phoneNumber ?? '').localeCompare(
                users[b.assignedToID ?? -1]?.phoneNumber ?? ''
              ),
          },
          {
            head: ' ',
            render: (row) => (
              <Centered>
                <CaseOpenLink caseID={row.caseID} />
              </Centered>
            ),
            width: 20,
          },

          {
            head: ' ',
            render: (row) => (
              <Centered>
                <TextButton style={{ padding: 0 }}>
                  <FontAwesomeIcon
                    onClick={() => {
                      modalStack.push(
                        <AvailabilityFormModal
                          userId={row.assignedToID ?? -1}
                          initialDate={row.bookedTo}
                          name={users[row.assignedToID ?? -1]?.name ?? ''}
                          onClose={() => modalStack.pop()}
                          onSaved={() => modalStack.pop()}
                          viewOnly
                          showUserFields
                        />
                      );
                    }}
                    icon={faCalendarDay}
                    fontSize="18px"
                    style={{ cursor: 'pointer' }}
                  />
                </TextButton>
              </Centered>
            ),
            width: 20,
          },

          {
            head: 'Klart',
            render: (row) => (
              <Centered>
                <Checkbox
                  disabled={markAsReadStatus === 'loading'}
                  onChange={async () => {
                    setOngoingSaves([...ongoingSaves, row.id]);
                    await markEventAsRead({ data: [row.id] });
                    setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
                  }}
                />
              </Centered>
            ),
            width: 30,
          },
        ]);
      return columns;
    }, [
      markAsReadStatus,
      markEventAsRead,
      modalStack,
      ongoingSaves,
      users,
      baseColumnSettings,
    ]);
  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.EstimatedEndTimePassed]}
      count={assignments.length}
      isLoading={getAssignments.isLoading}
      info="Fälttestaren har ännu inte avslutat sitt uppdrag enligt beräknade sluttiden utifrån estimerad starttid och estimerad uppdragstid."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredAssignments}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
          useColumnWidthAsFlexAmount
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default EstimatedEndTimePassedWidget;
